import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Box, List, ListItem, ListItemIcon, ListItemText, Collapse, IconButton, Typography, Divider, useTheme, Button, useMediaQuery, Drawer } from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  ShoppingCart as ShoppingCartIcon,
  Receipt as ReceiptIcon,
  Assessment as AssessmentIcon,
  ExpandLess,
  ExpandMore,
  ChevronLeft,
  ChevronRight,
  PointOfSale,
  Book,
  Print,
  Menu as MenuIcon,
  FlashOn as FlashOnIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { getCurrentUser, logout } from '../../services/authService';
import { APP_NAME } from '../../config/constants';

const drawerWidth = 240;
const collapsedDrawerWidth = 60;

const StyledSidebar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: open ? drawerWidth : collapsedDrawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  backgroundColor: '#1565c0',
  color: '#fff',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: '100vh', // Ensure full height
  position: 'sticky', // Keep it sticky
  top: 0,
  zIndex: 1200, // Ensure it's above other content if needed
}));

const Sidebar = ({ selectedMenu, onMenuSelect }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [prevIsMobile, setPrevIsMobile] = useState(isMobile);
  const [open, setOpen] = useState(!isMobile); // Default closed on mobile
  const [reportsOpen, setReportsOpen] = useState(false);

  const user = getCurrentUser();
  const rolesRaw = user?.roles || user?.authorities || user?.permissions || user?.role || [];
  const roles = Array.isArray(rolesRaw)
    ? rolesRaw.map(r => (typeof r === 'string' ? r.toLowerCase() : String(r?.authority || r?.name || '').toLowerCase()))
    : String(rolesRaw).toLowerCase();
  const hasAdminAccess = Array.isArray(roles)
    ? (roles.includes('admin') || roles.includes('super_user'))
    : (roles.includes('admin') || roles.includes('super_user'));

  if (isMobile !== prevIsMobile) {
    setPrevIsMobile(isMobile);
    setOpen(!isMobile);
  }

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleReportsClick = () => {
    setReportsOpen(!reportsOpen);
  };

  const handleItemClick = (key) => {
    onMenuSelect(key);
    if (isMobile) {
      setOpen(false);
    }
  };

  const menuItems = [
    { key: 'collections', text: 'Daily Collections', icon: <DashboardIcon /> },
    { key: 'instantBill', text: 'Instant Bill', icon: <FlashOnIcon /> },
    { key: 'customerBill', text: 'Customer Bill', icon: <ReceiptIcon /> },
    { key: 'quickBill', text: 'Quick Bill', icon: <PointOfSale /> },
    { key: 'productBill', text: 'Product Bill', icon: <ShoppingCartIcon /> },
    {
      key: 'reports',
      text: 'Reports',
      icon: <AssessmentIcon />,
      subItems: [
        { key: 'printDayBill', text: 'Print Day Bill', icon: <Print /> },
        { key: 'customerLedger', text: 'Customer Ledger', icon: <Book /> },
      ],
    },
    ...(hasAdminAccess ? [{ key: 'customers', text: 'Customers', icon: <PeopleIcon /> }] : []),
    { key: 'products', text: 'Products', icon: <ShoppingCartIcon /> },
  ];

  const sidebarContent = (
    <>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: open ? 'space-between' : 'center', padding: '10px' }}>
          {open && <Typography variant="h6">{APP_NAME}</Typography>}
          <IconButton onClick={handleDrawerToggle} sx={{ color: '#fff' }}>
            {open ? <ChevronLeft /> : <MenuIcon />}
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)' }} />
        <List>
          {menuItems.map((item) => {
            if (item.subItems) {
              return (
                <React.Fragment key={item.key}>
                  <ListItem button onClick={handleReportsClick}>
                    <ListItemIcon sx={{ color: '#fff' }}>{item.icon}</ListItemIcon>
                    {open && <ListItemText primary={item.text} />}
                    {open && (reportsOpen ? <ExpandLess /> : <ExpandMore />)}
                  </ListItem>
                  <Collapse in={reportsOpen && open} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.subItems.map((subItem) => (
                        <ListItem
                          button
                          key={subItem.key}
                          selected={selectedMenu === subItem.key}
                          onClick={() => handleItemClick(subItem.key)}
                          sx={{ pl: 4 }}
                        >
                          <ListItemIcon sx={{ color: '#fff' }}>{subItem.icon}</ListItemIcon>
                          <ListItemText primary={subItem.text} />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            }
            return (
              <ListItem
                button
                key={item.key}
                selected={selectedMenu === item.key}
                onClick={() => handleItemClick(item.key)}
              >
                <ListItemIcon sx={{ color: '#fff' }}>{item.icon}</ListItemIcon>
                {open && <ListItemText primary={item.text} />}
              </ListItem>
            );
          })}
        </List>
      </Box>
      <Box>
        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)' }} />
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: open ? 'flex-start' : 'center' }}>
          <Typography variant="body2" sx={{ display: open ? 'block' : 'none' }}>{user?.fullName || user?.username || 'User'}</Typography>
          <Button
            variant="text"
            sx={{ color: '#fff', p: open ? 1 : 'auto', mt: open ? 1 : 0 }}
            onClick={logout}
          >
            {open ? 'Logout' : <ChevronLeft />}
          </Button>
        </Box>
      </Box>
    </>
  );

  if (isMobile) {
    return (
      <>
        {!open && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: 60,
              height: 60,
              zIndex: 1400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.2,
              transition: 'opacity 0.3s ease',
              '&:hover': {
                opacity: 1,
                '& button': { backgroundColor: '#0d47a1 !important' }
              },
            }}
          >
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                bgcolor: '#1565c0',
                color: '#fff',
                boxShadow: 3,
                transition: 'background-color 0.3s',
                '&:hover': { bgcolor: '#0d47a1' }
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        )}
        <Drawer
          variant="temporary"
          open={open}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: '#1565c0',
              color: '#fff',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      </>
    );
  }

  return (
    <StyledSidebar open={open}>
      {sidebarContent}
    </StyledSidebar>
  );
};

export default Sidebar;
